import React, { FC, ReactNode, useCallback, useRef } from 'react';
import { useEffectOnce } from 'react-use';
import { styled } from 'styled-components';
import { TelemetryContext, useTracked } from '../../../@vendor/Posthog';
import { useObserverReturningStore } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Icon } from '../../../concepts/resources/Icon';
import { Button, Colors, Float, usePrompt } from '../../../ui';
import { ButtonToggle } from '../../../ui/ButtonToggle';
import { DateBadgeButton } from '../../../ui/DateBadgeButton';
import { useLocationValue } from '../../AppLocation';
import { useResolve } from '../../Container';
import { useDashboardVersion } from '../../account/AccountHooks';
import { Physical } from '../../canvas/entities/Physical';
import { useParticipant2 } from '../../canvas/participants/ParticipantHooks';
import { BlockWheel } from '../../canvas/tooling/ToolLayer';
import { useComponentAffected, useComponentObserver } from '../../canvas/visualization/ComponentObserver';
import { PopoutWidget } from '../../canvas/workspace/widgets/WidgetInterface';
import { SelectUserTableButton } from '../../common/SelectUserTableButton';
import { Divider, Flexer, Label, LinkButton } from '../../dashboard/elements/LayoutElements';
import { toTaskDueDate } from '../../dashboard/tasks/TaskActions';
import { computeTaskDueDateColor, formatTaskDueDate } from '../../dashboard/tasks/TaskDueDateCell';
import { TaskOpenAttr, TaskRecordAttr } from './TaskAssocations';
import { taskClose, tasksDelete, taskSetAssignee, taskSetDueDate, taskSetStatus } from './TaskCommands';

const Frame = styled.div`
    display: flex;
    flex-direction: column;
    gap: .5rem;
    border: solid 2px ${Colors.primary};
    border-radius: .375rem;
    padding: 1rem;

    ${ButtonToggle.Option} {
        padding: .25rem .75rem;
        font-size: 0.8125rem;
        font-weight: 500;
    }

    ${ButtonToggle.Track} {
        gap: .25rem;
    }

    ${Label} {
        margin-right: 1rem;
    }
`;

const SelectorGrid = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    grid-auto-rows: 2.5rem;
    gap: .5rem;
    align-items: center;
`;

const AssignmentWidgetFrame: FC<{ subject: Physical, onClose(): void, children: ReactNode }> = ({ subject, onClose, children }) => {
    const frameRef = useRef<HTMLDivElement>();
    const promptRef = usePrompt({
        name: 'task-prompt',
        ignoreEnter: true,
        onAction: onClose,
    });
    return <BlockWheel>
        <Flexer ref={promptRef} minWidth="12">
            <Float shadow="xl">
                <Frame ref={frameRef} >
                    {children}
                </Frame>
                {/* {frameRef.current && <CommentArrow {...{ frame: frameRef, subject }} />} */}
            </Float>
        </Flexer>
    </BlockWheel>;
};


const opts = [
    { label: 'TO DO', value: 'open', color: Colors.gray200 },
    { label: 'DONE', value: 'done', color: Colors.positive },
    { label: 'WON\'T DO', value: 'closed', color: Colors.rose800 },
];

const StatusSelector = ({ status, onChange }: { status: string, onChange: (next: string) => void }) => {
    const onChangeTracked = useTracked('editor.taskWidget.setStatus[status]', onChange);
    return <ButtonToggle variant="fancy" options={opts} selected={status} onChange={onChangeTracked} />;
};

const AssigneeSelector = ({ userId, onChange }: { userId: string, onChange: (next: string) => void }) => {
    const user = useObserverReturningStore(useParticipant2(userId));
    const onChangeTracked = useTracked('editor.taskWidget.setAssignee', onChange);
    return <SelectUserTableButton placeholder="Unassigned" value={user} onChange={x => onChangeTracked(x.id)} />;
};

const DueDateSelector = ({ dueDate, onChange }: { dueDate: string, onChange: (next: string) => void }) => {

    const value = typeof (dueDate) === 'string' ? new Date(dueDate) : null;
    const onChangeTracked = useTracked('editor.taskWidget.setDueDate', onChange);

    return <DateBadgeButton
        placeholder="No due date"
        formatter={formatTaskDueDate}
        color={computeTaskDueDateColor(dueDate)}
        value={value}
        onChange={x => onChangeTracked(toTaskDueDate(x))} />;
};

export const AssignmentWidget = ({ widget }: { widget: PopoutWidget }) => {

    const subject = widget.subject as Physical;
    const dispatch = useResolve(CommandDispatcher);
    const workspaceId = useLocationValue('workspace');
    const version = useDashboardVersion();

    useComponentObserver(subject);
    useComponentAffected(subject);

    useEffectOnce(() => void (TaskOpenAttr.setMaybe(subject, true)));

    const onClose = useCallback(() => {
        TaskOpenAttr.set(subject, false);
        dispatch(taskClose);
    }, [subject]);

    const { id: taskId, userId, dueDate, status } = TaskRecordAttr.get(subject) ?? {};

    const onSetStatus = useCallback((value: string) => {
        dispatch(taskSetStatus, [value, [subject.id]]);
    }, [subject]);

    const onSetAssignee = useCallback((value: string) => {
        dispatch(taskSetAssignee, [value, [subject.id]]);
    }, [subject]);

    const onSetDueDate = useCallback((value: string) => {
        dispatch(taskSetDueDate, [value, [subject.id]]);
    }, [subject]);

    const onDelete = useCallback(() => {
        dispatch(tasksDelete, [[subject.id]]);
        onClose();
    }, [subject]);

    return <TelemetryContext taskId={taskId}>
        <AssignmentWidgetFrame subject={subject} onClose={onClose}>
            <SelectorGrid>
                <Label>
                    Status:
                </Label>
                <StatusSelector status={status} onChange={onSetStatus} />
                <Label>
                    Assignee:
                </Label>
                <AssigneeSelector userId={userId} onChange={onSetAssignee} />
                <Label>
                    Due date:
                </Label>
                <DueDateSelector dueDate={dueDate} onChange={onSetDueDate} />
            </SelectorGrid>
            <Divider mode="horizontal" />
            <Flexer flex="row" align="center" justify="space-between" margin=".5 0 0">
                <Button data-action="editor.taskWidget.delete" size="xs" onAction={onDelete}>
                    Delete
                </Button>
                {workspaceId && version === 'next' &&
                    <LinkButton data-action="editor.taskWidget.openDashboard" href={`/${workspaceId}/tasks?highlight=${taskId}`} target="_blank" size="xs" variant="text">
                        Open Actions <Icon size="1" src="deqo://icons/hero/external-link" />
                    </LinkButton>
                }
            </Flexer>
        </AssignmentWidgetFrame>
    </TelemetryContext>;
};
